import axiosInstance from '../axiosInstance';
import { isAxiosError } from 'axios';
import { 
  Project, 
  Asset, 
  Risk, 
  RemediationPlan, 
  CorrectiveAction,
  ProjectVulnerability,
  ProjectThreat,
  ProjectTask,
  ProjectDocument
} from '../../types/project';

// Point vers l'API des projets - sans le préfixe '/api/' car axiosInstance l'ajoute déjà
// Point vers l'API des projets sans le préfixe /api/ car axiosInstance l'ajoute déjà
const API_ENDPOINT = '/projects';

// Variable pour déterminer quand utiliser l'API locale
let shouldUseMockApi = false;

// Fonction pour vérifier si la connexion au backend est disponible
const checkBackendConnection = async (): Promise<boolean> => {
  try {
    // Utiliser un endpoint qui existe certainement au lieu de /api/health
    const response = await axiosInstance.get('/current-user/', { timeout: 3000 });
    return response.status === 200 || response.status === 401 || response.status === 403;
  } catch (error) {
    console.warn('⚠️ Backend non disponible, utilisation des API locales');
    shouldUseMockApi = true; // Activer automatiquement les données mockées
    return false;
  }
};

// Base de données mockée pour les projets en cas de défaillance de l'API
const mockProjects: Project[] = [
  {
    id: 1,
    name: 'Audit de sécurité - Entreprise A',
    description: 'Évaluation complète de la sécurité de l\'infrastructure IT',
    status: 'IN_PROGRESS',
    priority: 'HIGH',
    completion_percentage: 65,
    start_date: '2025-01-15',
    deadline: '2025-06-30',
    created_at: '2025-01-10T10:00:00Z',
    updated_at: '2025-05-03T14:30:00Z',
    assets: [],
    vulnerabilities: [],
    tasks: [],
    team_members: [],
    owner: 1,
    is_active: true
  },
  {
    id: 2,
    name: 'Analyse des risques - Secteur bancaire',
    description: 'Identification et évaluation des risques de sécurité pour une institution financière',
    status: 'PLANNING',
    priority: 'CRITICAL',
    completion_percentage: 25,
    start_date: '2025-04-01',
    deadline: '2025-07-31',
    created_at: '2025-03-20T09:00:00Z',
    updated_at: '2025-05-02T11:15:00Z',
    assets: [],
    vulnerabilities: [],
    tasks: [],
    team_members: [],
    owner: 1,
    is_active: true
  },
  {
    id: 3,
    name: 'Tests d\'intrusion - Application web',
    description: 'Évaluation de la sécurité d\'une application web critique',
    status: 'COMPLETED',
    priority: 'MEDIUM',
    completion_percentage: 100,
    start_date: '2025-02-10',
    deadline: '2025-03-15',
    created_at: '2025-02-05T08:30:00Z',
    updated_at: '2025-03-15T16:45:00Z',
    assets: [],
    vulnerabilities: [],
    tasks: [],
    team_members: [],
    owner: 1,
    is_active: true
  }
];

// Données mockées pour les autres types
const mockAssets: Asset[] = [];
const mockRisks: Risk[] = [];
const mockThreats: ProjectThreat[] = [];
const mockVulnerabilities: ProjectVulnerability[] = [];
const mockTasks: ProjectTask[] = [];
const mockDocuments: ProjectDocument[] = [];
const mockRemediationPlans: RemediationPlan[] = [];
const mockCorrectiveActions: CorrectiveAction[] = [];

// Fonction générique pour gérer les appels API avec fallback
const handleApiRequest = async <T>(apiCall: () => Promise<T>, mockData: T): Promise<T> => {
  // Vérifier si on doit utiliser les données mockées dès le départ
  if (shouldUseMockApi) {
    console.log('🔄 Utilisation des données mockées (API locale)');
    return mockData;
  }

  // Créer une promesse avec timeout pour éviter les chargements infinis
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error('Timeout - L\'API a mis trop de temps à répondre'));
    }, 5000); // 5 secondes de timeout
  });

  try {
    // Utiliser Promise.race pour avoir soit la réponse soit le timeout
    const data = await Promise.race([
      apiCall(),
      timeoutPromise
    ]) as T;
    return data;
  } catch (error: any) {
    console.error('❌ Erreur lors de la requête API:', error);

    // Si l'erreur est 401/403, NE JAMAIS utiliser les mocks (authentification)
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.error('🔐 Erreur d\'authentification lors de l\'appel API');
      // On ne touche pas à shouldUseMockApi !
      // On relance l'erreur pour que le frontend affiche un vrai écran d'auth
      throw error;
    }

    // Si erreur réseau ou timeout, NE PAS masquer le problème avec les mocks
    if (
      error.message?.includes('Network Error') ||
      error.message?.includes('Timeout') ||
      !error.response
    ) {
      // On n'active plus shouldUseMockApi ici !
      // On relance une erreur explicite pour que le frontend affiche un écran d'erreur réseau
      throw new Error('Erreur de connexion au backend Sentinel. Merci de vérifier votre accès réseau ou de contacter l\'administrateur.');
    }

    // Pour les autres erreurs (500, etc.), afficher un message d'erreur explicite
    throw new Error('Erreur inattendue lors de l\'appel API Sentinel : ' + (error instanceof Error ? error.message : 'Erreur inconnue.'));
  }
};

export const projectsAPI = {
  // Projets
  getProjects: async (): Promise<Project[]> => {
    console.log('🔄 Récupération des projets...');
    return new Promise<Project[]>((resolve, reject) => {
      axiosInstance.get('/projects/')
        .then((response: any) => {
          // Nouvelle logique : gérer réponse paginée { count, results }
          if (response.data && Array.isArray(response.data.results)) {
            console.log('✅ Projets récupérés (format paginé):', response.data.results.length);
            resolve(response.data.results);
          } else if (Array.isArray(response.data)) {
            // Ancien format : tableau direct
            console.log('✅ Projets récupérés (format tableau):', response.data.length);
            resolve(response.data);
          } else {
            console.error('❌ Réponse inattendue de /projects/ :', response.data);
            resolve([]);
          }
        })
        .catch((error: unknown) => {
          console.error('❌ Erreur lors de la récupération des projets:', error instanceof Error ? error.message : 'Erreur inconnue');
          // Retourner une liste vide pour que l'UI puisse quand même fonctionner
          resolve([]);
        });
    });
  },

  
  getProjectById: async (id: number): Promise<Project> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/${id}/`);
        return response.data;
      },
      mockProjects.find(p => p.id === id) || mockProjects[0]
    );
  },
  
  createProject: async (projectData: Partial<Project>): Promise<Project> => {
    console.log('🔄 Création d\'un nouveau projet...');
    return new Promise<Project>((resolve, reject) => {
      axiosInstance.post('/projects/', projectData)
        .then((response: any) => {
          console.log('✅ Projet créé avec succès:', response.data);
          resolve(response.data);
        })
        .catch((error: unknown) => {
          console.error('❌ Erreur lors de la création du projet:', error instanceof Error ? error.message : 'Erreur inconnue');
          
          // Vérifier si c'est une erreur Axios pour accéder à error.response
          if (isAxiosError(error) && error.response?.data?.detail) {
            reject(new Error(`Erreur: ${error.response.data.detail}`));
          } else {
            reject(new Error('Erreur lors de la création du projet. Veuillez réessayer.'));
          }
        });
    })
  },
  
  updateProject: async (id: number, projectData: Partial<Project>): Promise<Project> => {
    return handleApiRequest(
      async () => {
        // Utilisation du chemin modifié avec API_ENDPOINT qui inclut déjà /api/
        const response = await axiosInstance.put(`/api/projects/${id}/`, projectData);
        return response.data;
      },
      {
        ...mockProjects.find(p => p.id === id) || mockProjects[0],
        ...projectData,
        updated_at: new Date().toISOString()
      } as Project
    );
  },
  
  deleteProject: async (id: number): Promise<void> => {
    return handleApiRequest(
      async () => {
        await axiosInstance.delete(`/api/projects/${id}/`);
      },
      undefined as void
    );
  },
  
  getProjectDashboard: async (id: number): Promise<Project> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`/api/projects/${id}/dashboard/`);
        return response.data;
      },
      mockProjects.find(p => p.id === id) || mockProjects[0]
    );
  },
  
  addTeamMember: async (projectId: number, userId: number): Promise<void> => {
    return handleApiRequest(
      async () => {
        await axiosInstance.post(`/api/projects/${projectId}/add_team_member/`, { user_id: userId });
      },
      undefined as void
    );
  },
  
  removeTeamMember: async (projectId: number, userId: number): Promise<void> => {
    return handleApiRequest(
      async () => {
        await axiosInstance.post(`/api/projects/${projectId}/remove_team_member/`, { user_id: userId });
      },
      undefined as void
    );
  },
  
  // Récupère la liste des membres d'un projet
  getProjectMembers: async (projectId: number): Promise<any[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`/api/projects/${projectId}/team-members/`);
        return response.data;
      },
      // Mock data pour les membres de l'équipe
      [
        {
          id: 1,
          name: 'Administrateur',
          email: 'admin@example.com',
          role: 'ADMIN',
          is_owner: true,
          last_active: new Date().toISOString()
        },
        {
          id: 2,
          name: 'Analyste Sécurité',
          email: 'analyste@example.com',
          role: 'MEMBER',
          is_owner: false,
          last_active: new Date().toISOString()
        },
        {
          id: 3,
          name: 'Consultant Externe',
          email: 'consultant@example.com',
          role: 'VIEWER',
          is_owner: false,
          last_active: new Date(new Date().setDate(new Date().getDate() - 5)).toISOString()
        }
      ]
    );
  },
  
  // Actifs
  getAssets: async (): Promise<Asset[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get('/api/assets/');
        return response.data;
      },
      mockAssets
    );
  },
  
  getAssetsByProject: async (projectId: number): Promise<Asset[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/assets/?project=${projectId}`);
        return response.data;
      },
      mockAssets.filter(a => a.project === projectId) || []
    );
  },
  
  getAssetById: async (id: number): Promise<Asset> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/assets/${id}/`);
        return response.data;
      },
      mockAssets.find(a => a.id === id) || {} as Asset
    );
  },
  
  createAsset: async (assetData: Partial<Asset>): Promise<Asset> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.post(`${API_ENDPOINT}/assets/`, assetData);
        return response.data;
      },
      {
        id: Math.floor(Math.random() * 1000) + 100,
        ...assetData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as Asset
    );
  },
  
  updateAsset: async (id: number, assetData: Partial<Asset>): Promise<Asset> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.put(`${API_ENDPOINT}/assets/${id}/`, assetData);
        return response.data;
      },
      {
        ...mockAssets.find(a => a.id === id) || {},
        ...assetData,
        updated_at: new Date().toISOString()
      } as Asset
    );
  },
  
  deleteAsset: async (id: number): Promise<void> => {
    return handleApiRequest(
      async () => {
        await axiosInstance.delete(`${API_ENDPOINT}/assets/${id}/`);
      },
      undefined as void
    );
  },
  
  getAssetVulnerabilities: async (assetId: number): Promise<ProjectVulnerability[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/assets/${assetId}/vulnerabilities/`);
        return response.data;
      },
      mockVulnerabilities.filter(v => v.asset === assetId) || []
    );
  },
  
  getAssetThreats: async (assetId: number): Promise<ProjectThreat[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/assets/${assetId}/threats/`);
        return response.data;
      },
      mockThreats.filter(t => t.asset === assetId) || []
    );
  },
  
  // Risques
  getRisks: async (): Promise<Risk[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/risks/`);
        return response.data;
      },
      mockRisks
    );
  },
  
  getRisksByProject: async (projectId: number): Promise<Risk[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/risks/?project=${projectId}`);
        return response.data;
      },
      mockRisks.filter(r => r.project === projectId) || []
    );
  },
  
  getRiskById: async (id: number): Promise<Risk> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/risks/${id}/`);
        return response.data;
      },
      mockRisks.find(r => r.id === id) || {} as Risk
    );
  },
  
  createRisk: async (riskData: Partial<Risk>): Promise<Risk> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.post(`${API_ENDPOINT}/risks/`, riskData);
        return response.data;
      },
      {
        id: Math.floor(Math.random() * 1000) + 100,
        ...riskData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as Risk
    );
  },
  
  updateRisk: async (id: number, riskData: Partial<Risk>): Promise<Risk> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.put(`${API_ENDPOINT}/risks/${id}/`, riskData);
        return response.data;
      },
      {
        ...mockRisks.find(r => r.id === id) || {},
        ...riskData,
        updated_at: new Date().toISOString()
      } as Risk
    );
  },
  
  deleteRisk: async (id: number): Promise<void> => {
    return handleApiRequest(
      async () => {
        await axiosInstance.delete(`${API_ENDPOINT}/risks/${id}/`);
      },
      undefined as void
    );
  },
  
  createRemediationPlan: async (planData: Partial<RemediationPlan>): Promise<RemediationPlan> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.post(`${API_ENDPOINT}/remediation-plans/`, planData);
        return response.data;
      },
      {
        id: Math.floor(Math.random() * 1000) + 100,
        ...planData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as RemediationPlan
    );
  },
  
  // Plans de remédiation
  getRemediationPlans: async (): Promise<RemediationPlan[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/remediation-plans/`);
        return response.data;
      },
      mockRemediationPlans
    );
  },
  
  getRemediationPlanByRisk: async (riskId: number): Promise<RemediationPlan> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/risks/${riskId}/remediation-plan/`);
        return response.data;
      },
      mockRemediationPlans.find(p => p.risk === riskId) || {} as RemediationPlan
    );
  },
  
  getRemediationPlanById: async (id: number): Promise<RemediationPlan> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/remediation-plans/${id}/`);
        return response.data;
      },
      mockRemediationPlans.find(p => p.id === id) || {} as RemediationPlan
    );
  },
  
  updateRemediationPlan: async (id: number, planData: Partial<RemediationPlan>): Promise<RemediationPlan> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.put(`${API_ENDPOINT}/remediation-plans/${id}/`, planData);
        return response.data;
      },
      {
        ...mockRemediationPlans.find(p => p.id === id) || {},
        ...planData,
        updated_at: new Date().toISOString()
      } as RemediationPlan
    );
  },
  
  deleteRemediationPlan: async (id: number): Promise<void> => {
    return handleApiRequest(
      async () => {
        await axiosInstance.delete(`${API_ENDPOINT}/remediation-plans/${id}/`);
      },
      undefined as void
    );
  },
  
  getRemediationPlanActions: async (planId: number): Promise<CorrectiveAction[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/remediation-plans/${planId}/actions/`);
        return response.data;
      },
      mockCorrectiveActions.filter(a => a.remediation_plan === planId) || []
    );
  },
  
  getCorrectiveActionsByPlan: async (planId: number): Promise<CorrectiveAction[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/remediation-plans/${planId}/actions/`);
        return response.data;
      },
      mockCorrectiveActions.filter(a => a.remediation_plan === planId) || []
    );
  },
  
  addCorrectiveAction: async (planId: number, actionData: Partial<CorrectiveAction>): Promise<CorrectiveAction> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.post(`${API_ENDPOINT}/remediation-plans/${planId}/actions/`, actionData);
        return response.data;
      },
      {
        id: Math.floor(Math.random() * 1000) + 100,
        remediation_plan: planId,
        ...actionData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as CorrectiveAction
    );
  },
  
  // Actions correctives
  createCorrectiveAction: async (actionData: Partial<CorrectiveAction>): Promise<CorrectiveAction> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.post(`${API_ENDPOINT}/corrective-actions/`, actionData);
        return response.data;
      },
      {
        id: Math.floor(Math.random() * 1000) + 100,
        ...actionData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as CorrectiveAction
    );
  },
  
  getCorrectiveActions: async (): Promise<CorrectiveAction[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/corrective-actions/`);
        return response.data;
      },
      mockCorrectiveActions
    );
  },
  
  getCorrectiveActionById: async (id: number): Promise<CorrectiveAction> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/corrective-actions/${id}/`);
        return response.data;
      },
      mockCorrectiveActions.find(a => a.id === id) || {} as CorrectiveAction
    );
  },
  
  updateCorrectiveAction: async (id: number, actionData: Partial<CorrectiveAction>): Promise<CorrectiveAction> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.put(`${API_ENDPOINT}/corrective-actions/${id}/`, actionData);
        return response.data;
      },
      {
        ...mockCorrectiveActions.find(a => a.id === id) || {},
        ...actionData,
        updated_at: new Date().toISOString()
      } as CorrectiveAction
    );
  },
  
  deleteCorrectiveAction: async (id: number): Promise<void> => {
    return handleApiRequest(
      async () => {
        await axiosInstance.delete(`${API_ENDPOINT}/corrective-actions/${id}/`);
      },
      undefined as void
    );
  },
  
  updateCorrectiveActionProgress: async (id: number, progress: number): Promise<CorrectiveAction> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.post(`${API_ENDPOINT}/corrective-actions/${id}/update_progress/`, { progress });
        return response.data;
      },
      {
        ...mockCorrectiveActions.find(a => a.id === id) || {},
        progress,
        updated_at: new Date().toISOString()
      } as CorrectiveAction
    );
  },
  
  // Vulnérabilités
  getVulnerabilities: async (): Promise<ProjectVulnerability[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/vulnerabilities/`);
        return response.data;
      },
      mockVulnerabilities
    );
  },
  
  getVulnerabilitiesByProject: async (projectId: number): Promise<ProjectVulnerability[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/vulnerabilities/?project=${projectId}`);
        return response.data;
      },
      mockVulnerabilities.filter(v => v.project === projectId) || []
    );
  },
  
  getVulnerabilityById: async (id: number): Promise<ProjectVulnerability> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/vulnerabilities/${id}/`);
        return response.data;
      },
      mockVulnerabilities.find(v => v.id === id) || {} as ProjectVulnerability
    );
  },
  
  createVulnerability: async (vulnerabilityData: Partial<ProjectVulnerability>): Promise<ProjectVulnerability> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.post(`${API_ENDPOINT}/vulnerabilities/`, vulnerabilityData);
        return response.data;
      },
      {
        id: Math.floor(Math.random() * 1000) + 100,
        ...vulnerabilityData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as ProjectVulnerability
    );
  },
  
  updateVulnerability: async (id: number, vulnData: Partial<ProjectVulnerability>): Promise<ProjectVulnerability> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.put(`${API_ENDPOINT}/vulnerabilities/${id}/`, vulnData);
        return response.data;
      },
      {
        ...mockVulnerabilities.find(v => v.id === id) || {},
        ...vulnData,
        updated_at: new Date().toISOString()
      } as ProjectVulnerability
    );
  },
  
  deleteVulnerability: async (id: number): Promise<void> => {
    return handleApiRequest(
      async () => {
        await axiosInstance.delete(`${API_ENDPOINT}/vulnerabilities/${id}/`);
      },
      undefined as void
    );
  },
  
  markVulnerabilityResolved: async (id: number): Promise<ProjectVulnerability> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.post(`${API_ENDPOINT}/vulnerabilities/${id}/mark_resolved/`);
        return response.data;
      },
      {
        ...mockVulnerabilities.find(v => v.id === id) || {},
        is_resolved: true,
        updated_at: new Date().toISOString()
      } as ProjectVulnerability
    );
  },
  
  // Menaces
  getThreats: async (): Promise<ProjectThreat[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/threats/`);
        return response.data;
      },
      mockThreats
    );
  },
  
  getThreatsByProject: async (projectId: number): Promise<ProjectThreat[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/threats/?project=${projectId}`);
        return response.data;
      },
      mockThreats.filter(t => t.project === projectId) || []
    );
  },
  
  getThreatById: async (id: number): Promise<ProjectThreat> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/threats/${id}/`);
        return response.data;
      },
      mockThreats.find(t => t.id === id) || {} as ProjectThreat
    );
  },
  
  createThreat: async (threatData: Partial<ProjectThreat>): Promise<ProjectThreat> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.post(`${API_ENDPOINT}/threats/`, threatData);
        return response.data;
      },
      {
        id: Math.floor(Math.random() * 1000) + 100,
        ...threatData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as ProjectThreat
    );
  },
  
  updateThreat: async (id: number, threatData: Partial<ProjectThreat>): Promise<ProjectThreat> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.put(`${API_ENDPOINT}/threats/${id}/`, threatData);
        return response.data;
      },
      {
        ...mockThreats.find(t => t.id === id) || {},
        ...threatData,
        updated_at: new Date().toISOString()
      } as ProjectThreat
    );
  },
  
  deleteThreat: async (id: number): Promise<void> => {
    return handleApiRequest(
      async () => {
        await axiosInstance.delete(`${API_ENDPOINT}/threats/${id}/`);
      },
      undefined as void
    );
  },
  
  markThreatMitigated: async (id: number): Promise<ProjectThreat> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.post(`${API_ENDPOINT}/threats/${id}/mark_mitigated/`);
        return response.data;
      },
      {
        ...mockThreats.find(t => t.id === id) || {},
        is_mitigated: true,
        updated_at: new Date().toISOString()
      } as ProjectThreat
    );
  },
  
  // Tâches
  getTasks: async (): Promise<ProjectTask[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/tasks/`);
        return response.data;
      },
      mockTasks
    );
  },
  
  getTasksByProject: async (projectId: number): Promise<ProjectTask[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/tasks/?project=${projectId}`);
        return response.data;
      },
      mockTasks.filter(t => t.project === projectId) || []
    );
  },
  
  getTaskById: async (id: number): Promise<ProjectTask> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/tasks/${id}/`);
        return response.data;
      },
      mockTasks.find(t => t.id === id) || {} as ProjectTask
    );
  },
  
  createTask: async (taskData: Partial<ProjectTask>): Promise<ProjectTask> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.post(`${API_ENDPOINT}/tasks/`, taskData);
        return response.data;
      },
      {
        id: Math.floor(Math.random() * 1000) + 100,
        ...taskData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as ProjectTask
    );
  },
  
  updateTask: async (id: number, taskData: Partial<ProjectTask>): Promise<ProjectTask> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.put(`${API_ENDPOINT}/tasks/${id}/`, taskData);
        return response.data;
      },
      {
        ...mockTasks.find(t => t.id === id) || {},
        ...taskData,
        updated_at: new Date().toISOString()
      } as ProjectTask
    );
  },
  
  deleteTask: async (id: number): Promise<void> => {
    return handleApiRequest(
      async () => {
        await axiosInstance.delete(`${API_ENDPOINT}/tasks/${id}/`);
      },
      undefined as void
    );
  },
  
  updateTaskProgress: async (id: number, progress: number): Promise<ProjectTask> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.post(`${API_ENDPOINT}/tasks/${id}/update_progress/`, { progress });
        return response.data;
      },
      {
        ...mockTasks.find(t => t.id === id) || {},
        progress,
        updated_at: new Date().toISOString()
      } as ProjectTask
    );
  },
  
  // Documents
  getDocuments: async (): Promise<ProjectDocument[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/documents/`);
        return response.data;
      },
      mockDocuments
    );
  },
  
  getDocumentsByProject: async (projectId: number): Promise<ProjectDocument[]> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/documents/?project=${projectId}`);
        return response.data;
      },
      mockDocuments.filter(d => d.project === projectId) || []
    );
  },
  
  getDocumentById: async (id: number): Promise<ProjectDocument> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.get(`${API_ENDPOINT}/documents/${id}/`);
        return response.data;
      },
      mockDocuments.find(d => d.id === id) || {} as ProjectDocument
    );
  },
  
  createDocument: async (formData: FormData): Promise<ProjectDocument> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.post(`${API_ENDPOINT}/documents/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        return response.data;
      },
      {
        id: Math.floor(Math.random() * 1000) + 100,
        name: formData.get('name')?.toString() || 'Document',
        file: formData.get('file')?.toString() || '',
        project: Number(formData.get('project')) || 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        title: formData.get('name')?.toString() || 'Document Title',
        description: 'Document généré automatiquement',
        document_type: 'OTHER',
        file_size: 1024,
        file_type: formData.get('file')?.toString()?.split('.').pop() || 'pdf'
      } as ProjectDocument
    );
  },
  
  updateDocument: async (id: number, formData: FormData): Promise<ProjectDocument> => {
    return handleApiRequest(
      async () => {
        const response = await axiosInstance.put(`${API_ENDPOINT}/documents/${id}/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        return response.data;
      },
      {
        ...mockDocuments.find(d => d.id === id) || {},
        name: formData.get('name')?.toString() || 'Document',
        file: formData.get('file')?.toString() || '',
        project: Number(formData.get('project')) || 0,
        updated_at: new Date().toISOString(),
        title: formData.get('name')?.toString() || 'Document Title',
        description: 'Document mis à jour automatiquement',
        document_type: 'OTHER',
        file_size: 1024,
        file_type: formData.get('file')?.toString()?.split('.').pop() || 'pdf'
      } as ProjectDocument
    );
  },
  
  deleteDocument: async (id: number): Promise<void> => {
    return handleApiRequest(
      async () => {
        await axiosInstance.delete(`${API_ENDPOINT}/documents/${id}/`);
      },
      undefined as void
    );
  },
};
